/**
 * useUndoRedo Hook
 * 
 * Provides undo/redo functionality for Team Builder using the Command Pattern.
 * Manages a history of reversible actions with a configurable size limit.
 * 
 * @example
 * const { canUndo, canRedo, executeAction, undo, redo } = useUndoRedo(50);
 * 
 * // Execute an action
 * executeAction(myAction);
 * 
 * // Undo last action
 * if (canUndo) undo();
 * 
 * // Redo last undone action
 * if (canRedo) redo();
 */

import { useState, useCallback, useMemo } from 'react';
import { createLogger } from '@/utils/logger/devLogger';

const logger = createLogger('useUndoRedo');

/**
 * Represents a single reversible action in Team Builder
 */
export interface TeamBuilderAction<T = any> {
  /** Unique identifier for this action */
  id: string;
  
  /** Type of action performed */
  type: ActionType;
  
  /** When the action occurred */
  timestamp: Date;
  
  /** Human-readable description for UI display */
  description: string;
  
  /** Apply this action (move forward) */
  execute: () => void;
  
  /** Revert this action (move backward) */
  undo: () => void;
  
  /** State snapshots before and after action */
  snapshot: {
    before: T;
    after: T;
  };
}

/**
 * All possible action types in Team Builder
 */
export type ActionType =
  | 'DRAG_DROP'
  | 'AUTO_GENERATE'
  | 'POSITION_CHANGE'
  | 'TEAM_CREATE'
  | 'TEAM_DELETE'
  | 'NOTES_CHANGE'
  | 'COACH_ASSIGN'
  | 'ATHLETE_PROMOTE'
  | 'ATHLETE_DEMOTE'
  | 'CROSSOVER_ADD'
  | 'CROSS_TEAM_MOVE'
  | 'PRIMARY_SWAP';

/**
 * Return type for useUndoRedo hook
 */
export interface UseUndoRedoReturn<T = any> {
  /** Can we undo? (history exists before current position) */
  canUndo: boolean;
  
  /** Can we redo? (history exists after current position) */
  canRedo: boolean;
  
  /** Total number of actions in history */
  historySize: number;
  
  /** Current position in history (0-indexed) */
  currentIndex: number;
  
  /** Execute a new action and add to history */
  executeAction: (action: TeamBuilderAction<T>) => void;
  
  /** Undo the last action */
  undo: () => void;
  
  /** Redo the last undone action */
  redo: () => void;
  
  /** Clear all history */
  clear: () => void;
  
  /** Get full history array (for debugging) */
  getHistory: () => TeamBuilderAction<T>[];
  
  /** Get description of action that would be undone */
  getUndoDescription: () => string | null;
  
  /** Get description of action that would be redone */
  getRedoDescription: () => string | null;
}

/**
 * Custom hook for managing undo/redo functionality
 * 
 * @param maxHistorySize Maximum number of actions to keep in history (default: 50)
 * @returns Object with undo/redo state and methods
 */
export function useUndoRedo<T = any>(maxHistorySize: number = 50): UseUndoRedoReturn<T> {
  // History array: stores all actions
  const [history, setHistory] = useState<TeamBuilderAction<T>[]>([]);
  
  // Current position in history (-1 = no history, 0 = first action, etc.)
  const [currentIndex, setCurrentIndex] = useState<number>(-1);

  /**
   * Execute a new action and add it to history
   * Clears any redo history (actions after current index)
   */
  const executeAction = useCallback((action: TeamBuilderAction<T>) => {
    logger.info('Executing action', {
      type: action.type,
      description: action.description,
      id: action.id,
    });

    // Execute the action
    action.execute();

    setHistory((prevHistory) => {
      // Remove any actions after current index (clear redo history)
      const newHistory = prevHistory.slice(0, currentIndex + 1);
      
      // Add new action
      newHistory.push(action);
      
      // Enforce max history size (circular buffer - remove oldest)
      if (newHistory.length > maxHistorySize) {
        newHistory.shift(); // Remove oldest action
        setCurrentIndex(maxHistorySize - 1); // Adjust index
        return newHistory;
      }
      
      setCurrentIndex(newHistory.length - 1);
      return newHistory;
    });
  }, [currentIndex, maxHistorySize]);

  /**
   * Undo the last action
   * Moves back one position in history
   */
  const undo = useCallback(() => {
    if (currentIndex < 0) {
      logger.warn('Cannot undo: no history available');
      return;
    }

    const action = history[currentIndex];
    
    logger.info('Undoing action', {
      type: action.type,
      description: action.description,
      id: action.id,
    });

    // Call the undo function
    action.undo();
    
    // Move back in history
    setCurrentIndex(currentIndex - 1);
  }, [history, currentIndex]);

  /**
   * Redo the last undone action
   * Moves forward one position in history
   */
  const redo = useCallback(() => {
    if (currentIndex >= history.length - 1) {
      logger.warn('Cannot redo: at end of history');
      return;
    }

    const action = history[currentIndex + 1];
    
    logger.info('Redoing action', {
      type: action.type,
      description: action.description,
      id: action.id,
    });

    // Call the execute function
    action.execute();
    
    // Move forward in history
    setCurrentIndex(currentIndex + 1);
  }, [history, currentIndex]);

  /**
   * Clear all history
   */
  const clear = useCallback(() => {
    logger.info('Clearing undo/redo history');
    setHistory([]);
    setCurrentIndex(-1);
  }, []);

  /**
   * Get full history array (for debugging)
   */
  const getHistory = useCallback(() => {
    return history;
  }, [history]);

  /**
   * Get description of action that would be undone
   */
  const getUndoDescription = useCallback((): string | null => {
    if (currentIndex < 0) return null;
    return history[currentIndex]?.description || null;
  }, [history, currentIndex]);

  /**
   * Get description of action that would be redone
   */
  const getRedoDescription = useCallback((): string | null => {
    if (currentIndex >= history.length - 1) return null;
    return history[currentIndex + 1]?.description || null;
  }, [history, currentIndex]);

  // Computed properties
  const canUndo = useMemo(() => currentIndex >= 0, [currentIndex]);
  const canRedo = useMemo(() => currentIndex < history.length - 1, [currentIndex, history.length]);
  const historySize = useMemo(() => history.length, [history.length]);

  return {
    canUndo,
    canRedo,
    historySize,
    currentIndex,
    executeAction,
    undo,
    redo,
    clear,
    getHistory,
    getUndoDescription,
    getRedoDescription,
  };
}

